import { useActionsTrack } from "@/_old/features/analytics";
import { createContext, useContext, useMemo } from "react";

/**
 * Tracking do agendamento de relatórios (plano PED-12415/12416).
 *
 * Os eventos `schedule_*` são os MESMOS na tela de Utilização e no chat da Lia — o plano
 * pede um único conjunto, não dois. Como `ScheduleForm`/`ScheduleListItem` são compartilhados
 * pelas duas superfícies, `surface`/`source` vêm de um contexto em volta do componente em vez
 * de prop repetida em cada callback: assim as props globais nunca faltam e o componente
 * compartilhado não precisa saber onde está sendo renderizado.
 */
export type ScheduleSurface = "myoffice_ui" | "ai_central";
export type ScheduleSource = "interface" | "lia";
export type ScheduleMode = "new" | "edit";

export interface ScheduleTrackingContextValue {
  surface: ScheduleSurface;
  source: ScheduleSource;
  /** Como o usuário chegou ao fluxo (ex.: `insights_header`, `agent_hub`). */
  entryPoint?: string;
}

const ScheduleTrackingContext = createContext<ScheduleTrackingContextValue>({
  surface: "myoffice_ui",
  source: "interface",
});

export const ScheduleTrackingProvider = ScheduleTrackingContext.Provider;

/** Sheet da tela de Utilização: manual. */
export const SHEET_TRACKING: ScheduleTrackingContextValue = {
  surface: "myoffice_ui",
  source: "interface",
  entryPoint: "insights_header",
};

/** Blocks renderizados no chat da Lia: IA. */
export const CHAT_TRACKING: ScheduleTrackingContextValue = {
  surface: "ai_central",
  source: "lia",
  entryPoint: "lia_chat",
};

/**
 * `report_type` é a própria `tool_key` do backend (hoje `utilizations`).
 *
 * O plano de tracking escrevia `utilization` no singular, mas mandamos a chave crua: é a mesma
 * string que a API usa, então o dado casa com o backend e uma tool nova entra no relatório sem
 * precisar de tradução aqui.
 */
export function toReportType(toolKey: string | undefined): string {
  return toolKey ?? "unknown";
}

/**
 * `error_type` do plano: `limit_reached | validation | network`.
 *
 * O erro vem do axios; 4xx de validação e falha sem `response` (offline/timeout) são os casos
 * que o plano quer separar. Qualquer outro cai em `unknown` para não inventar categoria.
 */
export function toErrorType(error: unknown): string {
  const status = (error as { response?: { status?: number } } | undefined)
    ?.response?.status;

  if (status === undefined) return "network";
  if (status === 422 || status === 400) return "validation";
  if (status === 409) return "limit_reached";
  return "unknown";
}

/** Nº de destinatários, sem os endereços: e-mail do usuário + telefone, quando houver. */
export function countRecipients(values: {
  channels: string[];
  phone?: string | null;
}): number {
  const hasPhone =
    values.channels.includes("whatsapp") && Boolean(values.phone);
  return 1 + (hasPhone ? 1 : 0);
}

/**
 * `changed_fields` da edição: quais campos o usuário mexeu, sem os valores.
 *
 * `originalFrequency`/`originalWeekday` vêm já convertidos pelo chamador (via
 * `fromApiFrequency`): na API a frequência é INTERVAL/CRON, e comparar com a string da UI
 * exigiria o mapper aqui dentro.
 */
export function changedFields(
  original: {
    tool_key: string;
    send_time: string;
    timezone: string;
    channels: string[];
    frequency: string;
    weekday?: string;
  },
  values: {
    toolKey: string;
    time: string;
    timezone: string;
    channels: string[];
    frequency: string;
    weekdays: string[];
  },
): string[] {
  const changed: string[] = [];

  if (original.tool_key !== values.toolKey) changed.push("report_type");
  if (original.send_time !== values.time) changed.push("send_time");
  if (original.timezone !== values.timezone) changed.push("timezone");
  if (original.frequency !== values.frequency) changed.push("frequency");
  if (original.weekday !== values.weekdays[0]) changed.push("weekday");

  const before = [...original.channels].sort().join(",");
  const after = [...values.channels].sort().join(",");
  if (before !== after) changed.push("channels");

  return changed;
}

/**
 * Nunca enviar PII (e-mail, telefone, texto livre) — só contagens, ids e enums.
 * O form tem telefone e e-mails à mão, então os helpers abaixo expõem apenas contagem.
 */
export function useScheduleTracking() {
  const { onActionClick } = useActionsTrack();
  const { surface, source, entryPoint } = useContext(ScheduleTrackingContext);

  return useMemo(() => {
    const globals = {
      surface,
      source,
      ...(entryPoint ? { entry_point: entryPoint } : {}),
    };

    /** Todos os `schedule_*` entram na categoria de Utilização (Insights). */
    const track = (
      event: Parameters<typeof onActionClick>[0],
      data?: Record<string, unknown>,
    ) => onActionClick(event, "INSIGHTS", { ...globals, ...data });

    return {
      surface,
      source,

      /**
       * Dois gatilhos distintos levam ao mesmo fluxo: abrir o agendamento (header da
       * Utilização) e iniciar o formulário (CTA da lista vazia). Na primeira criação o
       * usuário passa pelos dois, então `entry_point` os separa em vez de o evento sair
       * duplicado e indistinguível.
       */
      createStarted: (entryPointOverride?: string) =>
        track("SCHEDULE_CREATE_STARTED", {
          mode: "new",
          ...(entryPointOverride ? { entry_point: entryPointOverride } : {}),
        }),

      editClicked: (scheduleId: number) =>
        track("SCHEDULE_EDIT_CLICKED", {
          schedule_id: scheduleId,
          mode: "edit",
        }),

      reportSelected: (toolKey: string, mode: ScheduleMode) =>
        track("SCHEDULE_REPORT_SELECTED", {
          report_type: toReportType(toolKey),
          mode,
        }),

      frequencySelected: (
        frequency: string,
        toolKey: string,
        mode: ScheduleMode,
      ) =>
        track("SCHEDULE_FREQUENCY_SELECTED", {
          frequency,
          report_type: toReportType(toolKey),
          mode,
        }),

      channelSelected: (channel: string) =>
        track("SCHEDULE_CHANNEL_SELECTED", { channel }),

      whatsappConfirmViewed: (hasSavedNumber: boolean) =>
        track("SCHEDULE_WHATSAPP_CONFIRM_VIEWED", {
          has_saved_number: hasSavedNumber,
        }),

      /** Sem o número: só o fato de ter mudado. */
      whatsappNumberUpdated: () => track("SCHEDULE_WHATSAPP_NUMBER_UPDATED"),

      saved: (data: {
        scheduleId?: number;
        toolKey: string;
        frequency: string;
        weekday?: string;
        sendTime: string;
        timezone: string;
        channels: string[];
        recipientsCount: number;
      }) =>
        track("SCHEDULE_SAVED", {
          schedule_id: data.scheduleId,
          report_type: toReportType(data.toolKey),
          frequency: data.frequency,
          weekday: data.weekday,
          send_time: data.sendTime,
          timezone: data.timezone,
          channels: data.channels,
          recipients_count: data.recipientsCount,
          mode: "new",
        }),

      saveFailed: (errorType: string, toolKey: string, mode: ScheduleMode) =>
        track("SCHEDULE_SAVE_FAILED", {
          error_type: errorType,
          report_type: toReportType(toolKey),
          mode,
        }),

      updated: (scheduleId: number, changedFields: string[]) =>
        track("SCHEDULE_UPDATED", {
          schedule_id: scheduleId,
          changed_fields: changedFields,
          mode: "edit",
        }),

      /** Pausar e retomar: o plano só marca `paused` como Must. */
      paused: (scheduleId: number) =>
        track("SCHEDULE_PAUSED", { schedule_id: scheduleId }),

      deleteClicked: (scheduleId: number) =>
        track("SCHEDULE_DELETE_CLICKED", { schedule_id: scheduleId }),

      deleteConfirmed: (scheduleId: number) =>
        track("SCHEDULE_DELETE_CONFIRMED", { schedule_id: scheduleId }),
    };
  }, [onActionClick, surface, source, entryPoint]);
}
